/**
 * WarningsBanner — Phase 6c.2 per Frontend Spec §21.6.5.
 *
 * Renders the backend's `warnings[]` array (Spec §20.6.8) as a stack
 * of amber sticky cards above the engagement results tab strip. Each
 * card surfaces:
 *   • A short title from i18n (`warning.<CODE>.title`) — the only
 *     piece the frontend "translates".
 *   • The Big4-auditor-approved `messageTr` VERBATIM from the backend
 *     response (spec-authoritative — frontend never paraphrases).
 *   • A collapsible "Affected contracts" list when
 *     `affectedContractIds` is non-empty.
 *   • A session-only dismiss button (no localStorage persistence —
 *     auditor sees the warning again on every engagement open per
 *     Backend Spec §20.6.8 reasoning).
 *
 * If the `warnings` prop is empty/absent, nothing renders — the
 * caller can pass `result?.warnings ?? []` unconditionally.
 */

import { ChevronDown, ChevronUp, Info, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { buildContractDetailPath } from "@/pages/engagement/utils/contractDetailLink";
import type { WarningItem } from "@/types/engagement";

export interface WarningsBannerProps {
  warnings: WarningItem[];
  /**
   * Phase 6a.18b — engagement id used to construct per-contract detail
   * links for each entry in `affectedContractIds`. When absent (e.g.
   * standalone unit tests not rooted in an engagement), the banner
   * falls back to the Phase 6c.2 plain-text list — no broken links.
   */
  engagementId?: string;
}

export function WarningsBanner({
  warnings,
  engagementId,
}: WarningsBannerProps) {
  const [dismissed, setDismissed] = React.useState<Set<string>>(
    () => new Set(),
  );
  const visible = warnings.filter((w) => !dismissed.has(dismissKey(w)));
  if (visible.length === 0) return null;
  return (
    <div className="mb-4 space-y-1" data-testid="warnings-banner">
      {visible.map((w) => (
        <WarningCard
          key={dismissKey(w)}
          warning={w}
          engagementId={engagementId}
          onDismiss={() =>
            setDismissed((prev) => {
              const next = new Set(prev);
              next.add(dismissKey(w));
              return next;
            })
          }
        />
      ))}
    </div>
  );
}

/**
 * Phase BB5-1 — compact inline card. Default state is a single 48px row:
 *   ℹ <title> ......................................... [Detay ▾] [×]
 * Clicking "Detay" expands a panel underneath with the verbatim
 * `messageTr` + the affected-contract chips. Collapsed total height
 * matches StaleBanner so the two stack cleanly under the page header.
 */
function WarningCard({
  warning,
  engagementId,
  onDismiss,
}: {
  warning: WarningItem;
  engagementId?: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const hasAffected = warning.affectedContractIds.length > 0;

  return (
    <div
      className="rounded-md border-l-4 border-orange-500 bg-orange-500/10"
      data-testid={`warning-${warning.code}`}
    >
      <div className="flex items-center gap-3 px-4 py-2">
        <Info
          className="h-4 w-4 flex-shrink-0 text-orange-600 dark:text-orange-400"
          aria-hidden
        />
        <p className="flex-1 min-w-0 truncate text-sm font-medium text-foreground">
          {t(`warning.${warning.code}.title` as never) as string}
        </p>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 hover:underline dark:text-orange-300"
          data-testid={`warning-${warning.code}-detail-toggle`}
        >
          {t("warning.detail")}
          {expanded ? (
            <ChevronUp className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3" aria-hidden />
          )}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-200"
          aria-label={t("warning.dismiss")}
          data-testid={`warning-${warning.code}-dismiss`}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {expanded && (
        <div className="border-t border-orange-500/30 px-4 py-2 space-y-1">
          {/* Phase 6c.2 — `messageTr` is rendered VERBATIM from the backend
              per Spec §20.6.8 (auditor-approved TR text). Frontend NEVER
              paraphrases, substitutes, or translates this body. */}
          <p className="text-sm text-foreground">{warning.messageTr}</p>
          {hasAffected && (
            <p
              className="break-words text-xs text-muted-foreground"
              data-testid={`warning-${warning.code}-affected-list`}
            >
              <span className="font-medium">
                {t("warning.affectedContracts")} (
                {warning.affectedContractIds.length}):
              </span>{" "}
              {warning.affectedContractIds.map((cid, i) => (
                <React.Fragment key={cid}>
                  {engagementId ? (
                    <Link
                      to={buildContractDetailPath(engagementId, cid)}
                      className="underline hover:text-foreground"
                      data-testid={`warning-${warning.code}-contract-link-${cid}`}
                    >
                      {cid}
                    </Link>
                  ) : (
                    <span>{cid}</span>
                  )}
                  {i < warning.affectedContractIds.length - 1 && ", "}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Dismissal key: warning code + first affected contract id (when
 * present). Lets the auditor dismiss two warnings of the same code
 * that target different contracts independently.
 */
function dismissKey(w: WarningItem): string {
  return `${w.code}::${w.affectedContractIds[0] ?? ""}`;
}
